import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import enquiryImage from "../image/project_42_home.webp";

export default function ContactFormDialog({
  isFormOpen: open,
  setIsFormOpen: setOpen,
  onSuccess,
}) {
  const [loading, setLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [privacyConsent, setPrivacyConsent] = React.useState(false);

  const handleClose = () => {
    if (loading) return;
    setSubmitError("");
    setPhone("");
    setPrivacyConsent(false);
    setOpen(false);
  };

  const apiIp = process.env.REACT_APP_API_PUBLIC_AP;

  const handleFormSubmit = async (formData, form) => {
    const { name, email, phone, description } = formData;
    setLoading(true);
    setSubmitError("");

    try {
      const enquiry = {
        email,
        name,
        phoneNo: phone,
        description,
        privacyConsent: formData.privacyConsent === "true",
        source: "Website enquiry",
      };
      await axios.post(`${apiIp}/api/email/send-email`, enquiry);
      await axios.post(`${apiIp}/api/spreedsheet/sheet-data-update`, enquiry);
      form.reset();
      setPhone("");
      setPrivacyConsent(false);
      setOpen(false);
      onSuccess?.({
        title: "Enquiry received",
        message:
          "Thank you for reaching out. Our team has received your enquiry and will be in touch soon.",
      });
    } catch (error) {
      setSubmitError(
        error.response?.data?.message ||
          "We couldn't send your enquiry. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="enquiry-dialog-title"
      PaperProps={{
        component: "form",
        onSubmit: (event) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          handleFormSubmit(
            Object.fromEntries(formData.entries()),
            event.currentTarget
          );
        },
        sx: {
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 1, sm: 1.5 },
          boxShadow: "0 28px 90px rgba(20, 19, 16, 0.24)",
          backgroundColor: "#f6f4ef",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(23, 22, 19, 0.68)" },
      }}
    >
      <IconButton
        aria-label="Close enquiry form"
        onClick={handleClose}
        disabled={loading}
        sx={{
          position: "absolute",
          zIndex: 1,
          top: 14,
          right: 14,
          color: "#292720",
          backgroundColor: "rgba(246,244,239,0.9)",
          "&:hover": {
            backgroundColor: "#ffffff",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.78fr 1.22fr" },
            minHeight: { md: 570 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: { xs: 180, md: 570 },
              px: { xs: 3, sm: 5, md: 4 },
              pt: { xs: 4, md: 7 },
              pb: { xs: 3, md: 6 },
              color: "white",
              backgroundImage: `linear-gradient(155deg, rgba(25,24,21,0.86), rgba(25,24,21,0.96)), url(${enquiryImage})`,
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          >
            <Box>
              <Typography
                sx={{
                  mb: 2,
                  color: "#d5bd91",
                  fontSize: "0.66rem",
                  fontWeight: 600,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                HOMELY BUILDOFIY
              </Typography>
              <Typography
                id="enquiry-dialog-title"
                component="h2"
                sx={{
                  maxWidth: 400,
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: { xs: "2rem", md: "2.7rem" },
                  fontWeight: 400,
                  lineHeight: 1.08,
                }}
              >
                Let&apos;s make room for what matters.
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 390,
                  color: "rgba(255,255,255,0.76)",
                  fontSize: "0.9rem",
                  lineHeight: 1.75,
                }}
              >
                Tell us a little about your plans. Our team will help you take
                the next step.
              </Typography>
            </Box>
            <Typography
              sx={{
                display: { xs: "none", md: "block" },
                color: "rgba(255,255,255,0.62)",
                fontSize: "0.69rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Thoughtfully planned. Beautifully built.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: { xs: 3, sm: 5, md: 6 },
              py: { xs: 3, md: 6 },
            }}
          >
            <Typography
              sx={{
                mb: 1,
                color: "#9a794b",
                fontSize: "0.66rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              ENQUIRY
            </Typography>
            <Typography
              component="h3"
              sx={{
                mb: 1,
                color: "#292720",
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: "1.75rem",
                fontWeight: 400,
              }}
            >
              Start a conversation
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: "#706d64",
                fontSize: "0.85rem",
                lineHeight: 1.6,
              }}
            >
              Share your details and we&apos;ll get back to you shortly.
            </Typography>

            {submitError && (
              <Alert
                severity="error"
                onClose={() => setSubmitError("")}
                sx={{ mb: 2, borderRadius: 0 }}
              >
                {submitError}
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 2,
                rowGap: 1.8,
              }}
            >
              <TextField
                autoFocus
                required
                id="name"
                name="name"
                label="Full name"
                autoComplete="name"
                inputProps={{
                  maxLength: 120,
                  pattern: "[\\p{L}\\p{M}][\\p{L}\\p{M}'’ .-]*",
                  title: "Enter a name using letters, spaces, apostrophes, periods, or hyphens.",
                }}
                fullWidth
                variant="outlined"
                sx={fieldStyles}
              />
              <TextField
                required
                id="phone"
                name="phone"
                label="Phone number"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) =>
                  setPhone(
                    event.target.value
                      .replace(/[^0-9+(). -]/g, "")
                      .replace(/(?!^)\+/g, "")
                  )
                }
                inputProps={{
                  inputMode: "tel",
                  maxLength: 32,
                  pattern: "\\+?[0-9(). -]{6,31}",
                  title:
                    "Enter a phone number using 7 to 15 digits. You may include a leading +, spaces, parentheses, periods, or hyphens.",
                }}
                fullWidth
                variant="outlined"
                sx={fieldStyles}
              />
              <TextField
                required
                id="email"
                name="email"
                label="Email address"
                type="email"
                autoComplete="email"
                inputProps={{ maxLength: 254 }}
                fullWidth
                variant="outlined"
                sx={{ ...fieldStyles, gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                required
                id="description"
                name="description"
                label="How can we help?"
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 3000 }}
                variant="outlined"
                sx={{ ...fieldStyles, gridColumn: { sm: "1 / -1" } }}
              />
            </Box>

            <FormControlLabel
              required
              control={
                <Checkbox
                  name="privacyConsent"
                  value="true"
                  required
                  checked={privacyConsent}
                  onChange={(event) =>
                    setPrivacyConsent(event.target.checked)
                  }
                  sx={{
                    color: "#9a794b",
                    "&.Mui-checked": { color: "#9a794b" },
                  }}
                />
              }
              label="I agree that Homely Buildofiy may use my details to respond to this enquiry."
              sx={{
                mt: 1.8,
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  pt: 0.8,
                  color: "#706d64",
                  fontSize: "0.76rem",
                  lineHeight: 1.5,
                },
              }}
            />

            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                alignSelf: "flex-start",
                mt: 3,
                minWidth: 190,
                px: 2.5,
                py: 1.45,
                borderRadius: 0,
                backgroundColor: "#292720",
                color: "white",
                fontSize: "0.68rem",
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                "&:hover": { backgroundColor: "#49463d" },
                "& .MuiButton-endIcon": { ml: 1.5 },
              }}
            >
              Send enquiry
            </LoadingButton>
            <Typography
              sx={{
                mt: 2,
                color: "#8a877f",
                fontSize: "0.72rem",
                lineHeight: 1.5,
              }}
            >
              Your details will only be used to respond to your enquiry.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    backgroundColor: "rgba(255,255,255,0.72)",
    "& fieldset": { borderColor: "#d9d5ca" },
    "&:hover fieldset": { borderColor: "#a68757" },
    "&.Mui-focused fieldset": { borderColor: "#a68757", borderWidth: 1 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#8f7248" },
};
